package com.arena_web.data.api

import com.arena_web.data.dto.*
import com.arena_web.data.http.ApiResponse
import com.arena_web.data.http.apiDelete
import com.arena_web.data.http.apiDeleteNoBody
import com.arena_web.data.http.apiGet
import com.arena_web.data.http.apiPost
import com.arena_web.data.http.apiPostMultipart
import com.arena_web.data.http.apiPostNoBody
import com.arena_web.data.http.apiPut
import io.ktor.client.*
import io.ktor.http.*

class EventApi(private val client: HttpClient) {

    // ── Browse / CRUD ──
    suspend fun getEvents(params: Map<String, String> = emptyMap()): ApiResponse<EventsPageDto> {
        return client.apiGet("v1/events", params)
    }

    /** Distinct city names for today + future events (server-filtered). */
    suspend fun getUpcomingCities(sportId: String? = null): ApiResponse<List<String>> {
        val params = if (sportId.isNullOrBlank()) emptyMap() else mapOf("sportId" to sportId)
        return client.apiGet("v1/events/cities", params)
    }

    suspend fun getEvent(eventId: String): ApiResponse<EventDetailDto> {
        return client.apiGet("v1/events/$eventId")
    }

    suspend fun createEvent(request: CreateEventRequest): ApiResponse<EventDetailDto> {
        return client.apiPost("v1/events", request)
    }

    suspend fun updateEvent(eventId: String, request: UpdateEventRequest): ApiResponse<EventDetailDto> {
        return client.apiPut("v1/events/$eventId", request)
    }

    suspend fun deleteEvent(eventId: String): ApiResponse<Unit> {
        return client.apiDelete("v1/events/$eventId")
    }

    suspend fun updateEventStatus(eventId: String, request: UpdateEventStatusRequest): ApiResponse<EventDetailDto> {
        return client.apiPut("v1/events/$eventId/status", request)
    }

    // ── Participation ──
    // Action endpoints return the affected entity (participant or just status)
    // so the client can apply granular state updates instead of refetching the
    // whole event. Match the backend split in `EventRoute.kt`.
    suspend fun joinEvent(eventId: String): ApiResponse<EventParticipantDto> {
        return client.apiPost("v1/events/$eventId/join")
    }

    suspend fun leaveEvent(eventId: String): HttpStatusCode {
        return client.apiPostNoBody("v1/events/$eventId/leave")
    }

    suspend fun confirmParticipant(eventId: String, userId: String): ApiResponse<EventParticipantDto> {
        return client.apiPost("v1/events/$eventId/participants/$userId/confirm")
    }

    suspend fun rejectParticipant(eventId: String, userId: String): HttpStatusCode {
        return client.apiPostNoBody("v1/events/$eventId/participants/$userId/reject")
    }

    suspend fun assignTeam(eventId: String, request: AssignTeamRequest): ApiResponse<EventParticipantDto> {
        return client.apiPut("v1/events/$eventId/team", request)
    }

    suspend fun assignUserToTeam(eventId: String, userId: String, request: AssignTeamRequest): ApiResponse<EventParticipantDto> {
        return client.apiPut("v1/events/$eventId/participants/$userId/team", request)
    }

    suspend fun leaveTeam(eventId: String): ApiResponse<EventParticipantDto> {
        return client.apiDelete("v1/events/$eventId/team")
    }

    suspend fun removeUserFromTeam(eventId: String, userId: String): ApiResponse<EventParticipantDto> {
        return client.apiDelete("v1/events/$eventId/participants/$userId/team")
    }

    suspend fun setParticipantAdmin(eventId: String, userId: String, request: SetAdminRequest): ApiResponse<EventParticipantDto> {
        return client.apiPut("v1/events/$eventId/participants/$userId/admin", request)
    }

    // ── Teams & Scores ──
    suspend fun addTeam(eventId: String, request: AddEventTeamRequest): ApiResponse<EventTeamDto> {
        return client.apiPost("v1/events/$eventId/teams", request)
    }

    suspend fun deleteTeam(eventId: String, teamId: String): HttpStatusCode {
        return client.apiDeleteNoBody("v1/events/$eventId/teams/$teamId")
    }

    suspend fun setTeamScore(eventId: String, teamId: String, request: SetTeamScoreRequest): ApiResponse<Unit> {
        return client.apiPut("v1/events/$eventId/teams/$teamId/score", request)
    }

    // ── Team Invitations ──
    suspend fun createTeamInvitation(eventId: String, request: CreateTeamInvitationRequest): ApiResponse<TeamInvitationDto> {
        return client.apiPost("v1/events/$eventId/team-invitations", request)
    }

    suspend fun getTeamInvitations(eventId: String): ApiResponse<List<TeamInvitationDto>> {
        return client.apiGet("v1/events/$eventId/team-invitations")
    }

    suspend fun respondTeamInvitation(
        eventId: String,
        invitationId: String,
        request: RespondTeamInvitationRequest
    ): ApiResponse<Unit> {
        return client.apiPost("v1/events/$eventId/team-invitations/$invitationId/respond", request)
    }

    // ── Player Stats & Ratings ──
    suspend fun getStats(eventId: String): ApiResponse<EventPlayerStatsResultDto> {
        return client.apiGet("v1/events/$eventId/stats")
    }

    suspend fun setPlayerStats(
        eventId: String,
        userId: String,
        request: SetPlayerStatsRequest
    ): ApiResponse<Unit> {
        return client.apiPut("v1/events/$eventId/players/$userId/stats", request)
    }

    suspend fun swapLineup(eventId: String, request: SwapLineupRequest): ApiResponse<Unit> {
        return client.apiPost("v1/events/$eventId/lineup/swap", request)
    }

    suspend fun swapEventTeams(eventId: String, request: SwapEventTeamsRequest): ApiResponse<Unit> {
        return client.apiPost("v1/events/$eventId/teams/swap", request)
    }

    suspend fun getRatings(eventId: String): ApiResponse<List<EventPlayerRatingDto>> {
        return client.apiGet("v1/events/$eventId/ratings")
    }

    suspend fun ratePlayer(
        eventId: String,
        userId: String,
        request: RatePlayerRequest
    ): ApiResponse<EventPlayerRatingDto> {
        return client.apiPost("v1/events/$eventId/players/$userId/rate", request)
    }

    suspend fun toggleExtendedStats(
        eventId: String,
        request: ToggleExtendedStatsRequest
    ): ApiResponse<EventDetailDto> {
        return client.apiPut("v1/events/$eventId/extended-stats", request)
    }

    // ── Team Stats ──
    suspend fun getTeamStats(eventId: String): ApiResponse<EventTeamStatsResultDto> {
        return client.apiGet("v1/events/$eventId/team-stats")
    }

    suspend fun setTeamStats(
        eventId: String,
        teamId: String,
        request: SetTeamStatsRequest
    ): ApiResponse<Unit> {
        return client.apiPut("v1/events/$eventId/teams/$teamId/stats", request)
    }

    // ── Timer ──
    suspend fun getTimer(eventId: String): ApiResponse<EventTimerDto> {
        return client.apiGet("v1/events/$eventId/timer")
    }

    suspend fun updateTimer(eventId: String, request: EventTimerActionRequest): ApiResponse<EventTimerDto> {
        return client.apiPut("v1/events/$eventId/timer", request)
    }

    // ── Substitutions ──
    suspend fun getSubstitutions(eventId: String): ApiResponse<List<EventSubstitutionDto>> {
        return client.apiGet("v1/events/$eventId/substitutions")
    }

    suspend fun makeSubstitution(
        eventId: String,
        request: MakeSubstitutionRequest
    ): ApiResponse<EventSubstitutionDto> {
        return client.apiPost("v1/events/$eventId/substitutions", request)
    }

    // ── Media ──
    suspend fun getMedia(eventId: String): ApiResponse<List<EventMediaDto>> {
        return client.apiGet("v1/events/$eventId/media")
    }

    suspend fun deleteMedia(eventId: String, mediaId: String): ApiResponse<Unit> {
        return client.apiDelete("v1/events/$eventId/media/$mediaId")
    }

    suspend fun uploadMedia(
        eventId: String,
        fileBytes: ByteArray,
        fileName: String,
        contentType: String,
    ): ApiResponse<EventMediaDto> {
        return client.apiPostMultipart(
            "v1/events/$eventId/media",
            fileBytes,
            fileName,
            contentType,
        )
    }

    // ── Image Upload ──
    suspend fun uploadEventImage(
        fileBytes: ByteArray,
        fileName: String,
        contentType: String
    ): ApiResponse<ImageUploadResponse> {
        return client.apiPostMultipart("v1/events/upload-image", fileBytes, fileName, contentType)
    }
}
